import {
  Button,
  IconMedallion,
  InlineAlert,
  PageHeading,
  TextLink,
} from '@ai-tutor/ui';

import { XoneraSpinner } from '@/components/core/common/feedback/XoneraSpinner';

export type AuthStatusTone = 'info' | 'success' | 'error' | 'warning';

/**
 * The shell every non-form auth screen shares: check-your-email, verifying,
 * link-expired, account-restore, deactivated.
 *
 * One component instead of five hand-rolled cards is the point - those five
 * differed in medallion colour, medallion size (`h-12` vs `h-10`), icon size,
 * title size and footer shape, with no recorded reason for any of it.
 *
 * ## Left-aligned on purpose, against the house convention
 *
 * An audit of every status surface in the app (`EmptyState`, `ComingSoon`,
 * `PanelFallback`, `ErrorState`, `PanelGenerate`, `ViewerStates`, and the old
 * auth cards) comes out **14 of 15 centred**, with `rounded-full` as the only
 * icon-container shape anywhere in that set. By that precedent this "should" be
 * a centred column with a 48px circular disc.
 *
 * It was built that way and reverted, because side by side the left-aligned
 * version read better here - and it holds up for a reason: these status screens
 * share a card with the auth *forms*, which are left-aligned columns, so one
 * left edge means nothing shifts as a user moves from sign-up to
 * check-your-email. The 14 centred precedents are full-panel empty states with
 * no form beside them, which is a different situation. `EmptyState` in
 * `@ai-tutor/ui` is the centred one; this is deliberately not it.
 */
export function AuthStatusCard({
  tone = 'info',
  icon,
  title,
  body,
  children,
}: {
  tone?: AuthStatusTone;
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
  /** Actions, primary first. */
  children?: React.ReactNode;
}) {
  return (
    <div className='space-y-6'>
      <div className='space-y-3'>
        <IconMedallion tone={tone} size='md'>
          {icon}
        </IconMedallion>
        {/* `gap='loose'` keeps the 12px title-to-body rhythm this card has
            always had; the forms use the tight one. Same <h1> either way,
            which is the point - it used to be written out in both places. */}
        <PageHeading title={title} subtitle={body} gap='loose' />
      </div>
      {children && <div className='space-y-2.5'>{children}</div>}
    </div>
  );
}

/** A secondary, low-weight action under a status card's primary button. */
export function AuthStatusLink({
  onClick,
  children,
}: {
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    // `tap-target`: a 22px line under the card's button, tapped as 44 on a
    // phone.
    <TextLink block onClick={onClick} className='tap-target'>
      {children}
    </TextLink>
  );
}

/**
 * The primary action on a status card.
 *
 * Owns the pending spinner rather than leaving each caller to write
 * `{pending && <XoneraSpinner className='mr-2 h-4 w-4' label='' />}` before the
 * label - which five of them did, identically.
 */
export function AuthStatusButton({
  variant,
  onClick,
  disabled,
  pending = false,
  children,
}: {
  variant?: 'default' | 'outline';
  onClick?: () => void;
  disabled?: boolean;
  pending?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Button
      variant={variant}
      size='touch'
      fullWidth
      onClick={onClick}
      disabled={disabled || pending}
    >
      {pending && <XoneraSpinner className='mr-2 h-4 w-4' label='' />}
      {children}
    </Button>
  );
}

/** The trailing error on a status card, if its action failed. */
export function AuthStatusError({ message }: { message: string }) {
  return <InlineAlert message={message} />;
}
